<template>
  <div class="cadence-config">
    <h2>Session Cadence</h2>
    <p class="hint">
      Admin only. Controls how often each group gets a new session and which day
      their period starts. Cadence is <strong>append-only</strong>: a change is
      scheduled from a future date and never rewrites past periods. Existing
      policies are never edited or deleted, and only one change may be pending
      per group at a time.
    </p>


    <div class="split">
      <!-- ================= Left: every group's cadence ================= -->
      <div class="list-col">
        <!-- Header lives inside the bordered box, like the reminders table's
             own header, so it reads as the list's column heading rather than
             as a caption floating above it. -->
        <div class="list-panel">
          <div class="list-header">
            <span>Groups</span>
            <button type="button" class="link-btn" @click="load" :disabled="loading">
              Refresh
            </button>
          </div>

          <p v-if="loading" class="state pad">Loading groups…</p>
          <p v-else-if="error" class="lint-error pad">⚠ {{ error }}</p>
          <p v-else-if="!rows.length" class="state pad">No groups found.</p>

          <ul v-else class="group-list">
          <li
            v-for="r in rows"
            :key="r.group_id"
            :class="{
              selected: selected && selected.group_id === r.group_id,
              unconfigured: !r.current,
            }"
            @click="openDetail(r)"
          >
            <div class="g-head">
              <span class="g-name">{{ r.group_name }}</span>
              <span v-if="r.current" class="g-cadence">
                {{ fmtInterval(r.current.interval_weeks) }} ·
                {{ WEEKDAYS[r.current.start_day].slice(0, 3) }}
              </span>
              <span v-else class="g-cadence warn">not configured</span>
            </div>
            <div v-if="r.current" class="g-sub">
              {{ r.blockLabel }} · next opens {{ r.nextOpenLabel }}
            </div>
            <div v-else class="g-sub warn">
              Messaging and the session gate have nothing to go on.
            </div>
            <div v-if="r.next_change" class="g-pending">
              <span class="badge">Scheduled</span>
              {{ fmtInterval(r.next_change.interval_weeks) }},
              {{ WEEKDAYS[weekdayOf(r.next_change.effective_from)] }} start, from
              {{ fmtDate(r.next_change.effective_from, true) }}
            </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- ============ Right: detail / schedule for the selection ============ -->
      <div class="detail-col">
        <div v-if="!selected" class="empty-state">
          <span class="empty-icon" aria-hidden="true">🗓</span>
          <p class="empty-title">No group selected</p>
          <p class="empty-sub">
            Pick a group on the left to see the cadence in force, its full
            policy history, and to schedule a change.
          </p>
        </div>

        <!-- ---- Detail ---- -->
        <template v-else-if="pane === 'detail'">
          <div class="label-row">
            <label class="pane-title">
              <span class="pane-title-key">Group Name:</span>
              {{ selected.group_name }}
            </label>
            <button
              type="button"
              class="btn primary small"
              :disabled="!!pendingChange"
              @click="openForm"
            >
              Schedule change
            </button>
          </div>

          <p v-if="pendingChange" class="sub-hint pending-note">
            A change is already scheduled for
            <strong>{{ fmtDate(pendingChange.effective_from, true) }}</strong>.
            Only one change may be pending at a time — withdraw it below to
            schedule a different one.
          </p>

          <div v-if="currentPolicy" class="summary">
            <div>
              <span class="k">Total</span>
              <span class="v">{{ countLabel(selected.total_users) }}</span>
            </div>
            <div>
              <span class="k">Activated</span>
              <span class="v">{{ countLabel(selected.active_users) }}</span>
            </div>
            <div>
              <span class="k">Onboarded</span>
              <span class="v">{{ countLabel(selected.onboarded_users) }}</span>
            </div>
            <div>
              <span class="k">Interval</span>
              <span class="v">{{ fmtInterval(currentPolicy.interval_weeks) }}</span>
            </div>
            <div>
              <span class="k">Starts</span>
              <span class="v">{{ WEEKDAYS[currentPolicy.start_day] }}</span>
            </div>
            <div>
              <span class="k">Current period</span>
              <span class="v">{{ selected.blockLabel }}</span>
            </div>
            <div>
              <span class="k">Next opens</span>
              <span class="v">{{ selected.nextOpenLabel }}</span>
            </div>
          </div>
          <p v-else class="lint-error">
            ⚠ No cadence policy. Schedule one before this group runs sessions.
          </p>

          <p class="sub-hint history-note">
            A policy that has taken effect stays forever, because it still
            defines every period in its own date range.
          </p>

          <!-- Same header treatment as the groups list beside it; these two
               scroll boxes sit side by side and should not read differently. -->
          <div class="list-panel">
            <div class="list-header"><span>Policy history</span></div>
            <p v-if="historyLoading" class="state pad">Loading history…</p>
            <div v-else class="history-scroll">
              <ul class="history">
              <li v-for="p in history" :key="p.id" :class="p.status">
                <span class="dot"></span>
                <div class="h-main">
                  <span class="status-tag">{{ statusLabel(p.status) }}</span>
                  <span class="h-detail">
                    {{ fmtInterval(p.interval_weeks) }} ·
                    {{ WEEKDAYS[p.start_day] }} · from
                    {{ fmtDate(p.effective_from, true) }}
                  </span>
                  <span class="who">{{ p.created_by }}</span>
                  <button
                    v-if="!p.locked"
                    type="button"
                    class="btn danger small"
                    @click="withdraw(p)"
                  >
                    Withdraw
                  </button>
                  <span v-else class="locked" title="Already in effect — immutable">🔒</span>
                </div>
                  <p v-if="p.note" class="note">{{ p.note }}</p>
                </li>
              </ul>
            </div>
          </div>
        </template>

        <!-- ---- Schedule form ---- -->
        <template v-else>
          <div class="label-row">
            <label class="pane-title">Schedule change · {{ selected.group_name }}</label>
            <button type="button" class="link-btn" @click="pane = 'detail'">
              Back to history
            </button>
          </div>

          <div class="row">
            <div class="field">
              <label>New interval</label>
              <select v-model.number="form.interval_weeks" @change="requestPreview">
                <option v-for="i in INTERVALS" :key="i" :value="i">
                  {{ fmtInterval(i) }}
                </option>
              </select>
            </div>

            <div class="field">
              <label>Period starts on</label>
              <select v-model.number="form.start_day" @change="onStartDayChange">
                <option v-for="(d, i) in WEEKDAYS" :key="i" :value="i">{{ d }}</option>
              </select>
            </div>

            <div class="field">
              <label>Effective from</label>
              <input
                type="date"
                v-model="form.effective_from"
                :min="minDate"
                @change="onDateChange"
              />
            </div>
          </div>

          <p class="sub-hint">
            The start day is not stored separately — it is read off the effective
            date's weekday, so the two are kept in step.
          </p>

          <div class="field">
            <label>Note (optional)</label>
            <input
              type="text"
              v-model="form.note"
              placeholder="Why this change is being made"
            />
          </div>

          <div class="field">
            <label>Preview</label>
            <p v-if="previewLoading" class="state">Checking…</p>
            <CadenceTimeline
              v-else
              :outgoing="preview && preview.transition ? preview.transition.outgoing_blocks : []"
              :orphan="preview && preview.transition ? preview.transition.orphan : null"
              :incoming="preview && preview.transition ? preview.transition.incoming_blocks : []"
            />
          </div>

          <ul v-if="violations.length" class="violations">
            <li v-for="(v, i) in violations" :key="i" :class="v.severity">
              <strong>{{ v.severity === "error" ? "Blocked:" : "Warning:" }}</strong>
              {{ v.message }}
              <button
                v-if="v.snap_to"
                type="button"
                class="link-btn"
                @click="snapTo(v.snap_to)"
              >
                Snap to {{ fmtDate(v.snap_to, true) }}
              </button>
            </li>
          </ul>

          <div v-if="preview" class="field">
            <label>Participant impact</label>
            <p v-if="impactUnavailable" class="state">
              Not available in this preview build.
              {{ preview.impact.unavailable_reason }}
            </p>
            <div v-else class="impact">
              <div>
                <span class="n">{{ preview.impact.affected_users }}</span>
                <span class="k">affected</span>
              </div>
              <div>
                <span class="n">{{ preview.impact.would_get_early_session }}</span>
                <span class="k">early session</span>
              </div>
              <div>
                <span class="n">{{ preview.impact.would_be_locked_out }}</span>
                <span class="k">locked out</span>
              </div>
            </div>
          </div>

          <label v-if="hasWarnings && !hasErrors" class="confirm-toggle">
            <input type="checkbox" v-model="acknowledged" />
            I understand this truncates a period already in progress.
          </label>

          <div class="button-row">
            <button type="button" class="btn secondary" @click="pane = 'detail'">
              Cancel
            </button>
            <button
              type="button"
              class="btn primary"
              :disabled="!canSubmit"
              @click="submit"
            >
              {{ submitting ? "Scheduling…" : "Schedule change" }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
// Admin page for per-group session cadence. Implements the views in
// docs/WEEK_INTERVAL_AND_WEEK_START.md Part D.6 as a master/detail split: the
// group list stays on screen while a single group's history or schedule form
// is open on the right, so an admin can compare cadences across groups while
// deciding on one.
//
// The safety model this page exists to enforce: cadence policy is append-only
// and future-dated. Editing a cadence in place would silently move every past
// period boundary, changing which block a completed session belonged to --
// granting some users an extra session and locking others out. So there is no
// edit control anywhere here. The only write is "schedule a new policy from a
// future date", and the only deletion is withdrawing a row that has not yet
// taken effect.
import cadenceApi from "../services/cadence_api";
import swal from "../services/swal";
import CadenceTimeline from "./CadenceTimeline.vue";
import {
  addDays,
  blockForPolicy,
  formatBlock,
  formatDate,
  formatInterval,
  parseDate,
  policyHistory,
  toISO,
  today,
  weekdayIndex,
  WEEKDAYS,
} from "../utils/cadence";

// Selectable intervals. Extending the study to a 3- or 4-week protocol is a
// one-line change here; nothing else in the page assumes 1 or 2.
const INTERVALS = [1, 2];

const PREVIEW_DEBOUNCE_MS = 300;

export default {
  name: "SessionCadencePanel",
  components: { CadenceTimeline },
  props: {
    // Set by the parent tab strip. The page fetches on first activation rather
    // than on mount, so an admin who never opens this tab pays nothing.
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      WEEKDAYS,
      INTERVALS,
      rows: [],
      loading: false,
      loaded: false,
      error: "",
      // Right-hand pane: "detail" (history) or "form" (schedule a change).
      pane: "detail",
      selected: null,
      history: [],
      historyLoading: false,
      form: { interval_weeks: 2, start_day: 0, effective_from: "", note: "" },
      preview: null,
      previewLoading: false,
      previewTimer: null,
      acknowledged: false,
      submitting: false,
    };
  },
  computed: {
    // Tomorrow. The effective date must be strictly in the future, so today is
    // already too late.
    minDate() {
      return toISO(addDays(today(), 1));
    },
    currentPolicy() {
      return this.selected ? this.selected.current : null;
    },
    // At most one change may be pending, so this is the whole picture rather
    // than the first of several. The server enforces the same rule; see
    // utils/cadence.py, invariant 7.
    pendingChange() {
      return this.selected ? this.selected.next_change : null;
    },
    violations() {
      return this.preview ? this.preview.violations || [] : [];
    },
    hasErrors() {
      return this.violations.some((v) => v.severity === "error");
    },
    hasWarnings() {
      return this.violations.some((v) => v.severity === "warning");
    },
    impactUnavailable() {
      const impact = this.preview && this.preview.impact;
      return !impact || !!impact.unavailable_reason;
    },
    canSubmit() {
      if (this.submitting || this.previewLoading) return false;
      if (!this.preview || this.hasErrors) return false;
      // Warnings are passable, but only deliberately.
      return this.hasWarnings ? this.acknowledged : true;
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(on) {
        if (on && !this.loaded) this.load();
      },
    },
  },
  methods: {
    fmtInterval: formatInterval,

    fmtDate(iso, withYear = false) {
      return formatDate(parseDate(iso), { withYear });
    },

    weekdayOf(iso) {
      const d = parseDate(iso);
      return d ? weekdayIndex(d) : 0;
    },

    // Three separate columns rather than one combined figure, because they
    // answer different questions: Total is everyone assigned to the group,
    // Activated is users.is_active (the flag outbound_message filters on, so
    // the population a cadence change actually reaches), and Onboarded also
    // requires a confirmed phone. They diverge widely, so collapsing them into
    // one string hid the gap that matters.
    countLabel(n) {
      return n == null ? "—" : String(n);
    },

    statusLabel(s) {
      return { scheduled: "Scheduled", active: "Active", superseded: "Superseded" }[s];
    },

    async load() {
      this.loading = true;
      this.error = "";
      try {
        const raw = await cadenceApi.listCadence();
        this.rows = raw.map((r) => this.decorate(r));
        this.loaded = true;
        // Keep the right-hand pane pointed at the same group after a refresh.
        if (this.selected) {
          const again = this.rows.find((r) => r.group_id === this.selected.group_id);
          this.selected = again || null;
        }
      } catch (e) {
        this.error = `Could not load cadence settings. ${e?.message || e}`;
      } finally {
        this.loading = false;
      }
    },

    // Attach the derived period labels. The backend returns the policy; which
    // block today falls in is a pure function of it, so it is computed here
    // rather than round-tripped.
    decorate(r) {
      if (!r.current) return { ...r, blockLabel: "—", nextOpenLabel: "—" };
      const block = blockForPolicy(today(), r.current);
      return {
        ...r,
        blockLabel: block.beforeAnchor ? "Not started" : formatBlock(block),
        nextOpenLabel: block.beforeAnchor
          ? this.fmtDate(r.current.effective_from)
          : formatDate(block.end),
      };
    },

    async openDetail(r) {
      this.selected = r;
      this.pane = "detail";
      this.historyLoading = true;
      try {
        const raw = await cadenceApi.history(r.group_id);
        this.history = policyHistory(raw, today());
      } catch (e) {
        this.history = [];
        swal.fire({
          icon: "error",
          title: "Could not load history",
          text: String(e?.message || e),
        });
      } finally {
        this.historyLoading = false;
      }
    },

    openForm() {
      // Default to the current interval and start day, so "same cadence, later"
      // is the zero-effort baseline and every field the admin touches is a
      // deliberate change.
      const cur = this.currentPolicy;
      const startDay = cur ? cur.start_day : 0;
      this.form = {
        interval_weeks: cur ? cur.interval_weeks : 2,
        start_day: startDay,
        effective_from: this.nextWeekdayOnOrAfter(addDays(today(), 1), startDay),
        note: "",
      };
      this.preview = null;
      this.acknowledged = false;
      this.pane = "form";
      this.requestPreview();
    },

    // First date >= `from` whose weekday is `startDay`, as an ISO string.
    nextWeekdayOnOrAfter(from, startDay) {
      const delta = (startDay - weekdayIndex(from) + 7) % 7;
      return toISO(addDays(from, delta));
    },

    // The start-day select and the date picker are kept in lockstep, because
    // the weekday of the effective date IS the start day -- they cannot be set
    // independently without contradicting each other. Native <input type="date">
    // cannot restrict selectable weekdays, so instead of rejecting a mismatch
    // we move the other field to match.
    onStartDayChange() {
      const cur = parseDate(this.form.effective_from) || addDays(today(), 1);
      this.form.effective_from = this.nextWeekdayOnOrAfter(cur, this.form.start_day);
      this.requestPreview();
    },

    onDateChange() {
      const d = parseDate(this.form.effective_from);
      if (d) this.form.start_day = weekdayIndex(d);
      this.requestPreview();
    },

    snapTo(iso) {
      this.form.effective_from = iso;
      this.onDateChange();
    },

    requestPreview() {
      // Any field edit invalidates a previous acknowledgement: the admin must
      // re-confirm against the warnings that apply to what is now selected.
      this.acknowledged = false;
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(this.runPreview, PREVIEW_DEBOUNCE_MS);
    },

    async runPreview() {
      if (!this.selected) return;
      this.previewLoading = true;
      try {
        this.preview = await cadenceApi.preview(this.selected.group_id, {
          interval_weeks: this.form.interval_weeks,
          start_day: this.form.start_day,
          effective_from: this.form.effective_from,
        });
      } catch (e) {
        this.preview = null;
        swal.fire({
          icon: "error",
          title: "Could not preview the change",
          text: String(e?.message || e),
        });
      } finally {
        this.previewLoading = false;
      }
    },

    async submit() {
      if (!this.canSubmit) return;
      const when = this.fmtDate(this.form.effective_from, true);
      const confirm = await swal.fire({
        icon: this.hasWarnings ? "warning" : "question",
        title: `Schedule change from ${when}?`,
        text:
          `${this.selected.group_name} moves to ` +
          `${this.fmtInterval(this.form.interval_weeks)} starting on ` +
          `${WEEKDAYS[this.form.start_day]}s, effective ${when}. ` +
          "Periods before that date are unaffected." +
          (this.hasWarnings
            ? "\n\nThis truncates a period already in progress."
            : ""),
        showCancelButton: true,
        confirmButtonText: "Schedule",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;

      this.submitting = true;
      try {
        // The server re-runs the same validation and is the real gate; the
        // preview above is advisory only.
        await cadenceApi.schedule(this.selected.group_id, {
          interval_weeks: this.form.interval_weeks,
          start_day: this.form.start_day,
          effective_from: this.form.effective_from,
          note: this.form.note || null,
        });
        await this.load();
        if (this.selected) await this.openDetail(this.selected);
        swal.fire({
          icon: "success",
          title: "Scheduled",
          text: `The new cadence takes effect on ${when}.`,
        });
      } catch (e) {
        swal.fire({
          icon: "error",
          title: "Could not schedule",
          text: String(e?.message || e),
        });
      } finally {
        this.submitting = false;
      }
    },

    async withdraw(p) {
      const when = this.fmtDate(p.effective_from, true);
      const confirm = await swal.fire({
        icon: "warning",
        title: `Withdraw the change effective ${when}?`,
        text:
          "It has not taken effect yet, so nothing that already happened is " +
          "affected. This is the only kind of cadence row that can be removed.",
        showCancelButton: true,
        confirmButtonText: "Withdraw",
        cancelButtonText: "Keep",
      });
      if (!confirm.isConfirmed) return;

      try {
        await cadenceApi.withdraw(this.selected.group_id, p.id);
        await this.load();
        if (this.selected) await this.openDetail(this.selected);
      } catch (e) {
        swal.fire({
          icon: "error",
          title: "Could not withdraw",
          text: String(e?.message || e),
        });
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this.previewTimer);
  },
};
</script>

<style scoped>
/* Mirrors .model-config in ModelConfigPanel.vue: square top-left so the panel
   joins the tab strip above it, no side margins (the page shell owns the
   horizontal inset), and the same field/button vocabulary throughout. */
.cadence-config {
  /* Fixed so switching tabs does not jump the page height. Tuned to
     ModelConfigPanel's content height, which is dominated by its two
     textareas (rows=14 and rows=5) and is therefore effectively constant.
     If the editor's field list changes, this is the single value to retune. */
  --cadence-panel-height: 1030px;

  height: var(--cadence-panel-height);
  /* Column layout so .split can absorb whatever height the header above it
     does not use -- the mock banner disappears once the backend lands, and
     the panel must not change height when it does. */
  display: flex;
  flex-direction: column;
  padding: 20px;
  background-color: #ffffffb2;
  border-radius: 0 20px 20px 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin: 0 0 20px;
  max-width: none;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}

.hint {
  font-size: 0.9em;
  color: #444;
  margin-bottom: 16px;
}

.sub-hint {
  font-size: 0.86em;
  color: #666;
  margin: 0 0 12px;
}

/* Explains why the Schedule change button next to it is disabled. */
.pending-note {
  background: #fff8e6;
  border: 1px solid #f0c36d;
  border-radius: 4px;
  color: #7a5b00;
  padding: 8px 12px;
  margin-top: 4px;
}

.state {
  font-size: 0.85em;
  color: #666;
  margin: 6px 0;
}

/* Resting state of the right-hand pane, so it reads as a deliberate part of
   the layout rather than an area that failed to load. A dashed outline was
   the wrong cue -- it suggests a drop target or unfinished work. */
/* flex:1 so the resting pane fills the panel's fixed height and its message
   sits centred, rather than clinging to the top with dead space beneath. */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 64px 28px;
  background: #f7f8f9;
  border: 1px solid #e6e8ea;
  border-radius: 4px;
}

.empty-icon {
  font-size: 2em;
  line-height: 1;
  opacity: 0.45;
  margin-bottom: 12px;
}

.empty-title {
  font-size: 1.02em;
  font-weight: 600;
  color: #4a5460;
  margin: 0 0 6px;
}

.empty-sub {
  font-size: 0.88em;
  color: #6b7480;
  margin: 0;
  max-width: 42ch;
  line-height: 1.5;
}

.lint-error {
  color: #d33;
  font-size: 0.82em;
  margin: 6px 0 0;
}

/* Master/detail: the group list stays visible while one group is open, so
   cadences can be compared without navigating away. */
/* Fills the panel's remaining height. min-height:0 is required or the flex
   children refuse to shrink below their content and scroll never engages. */
.split {
  display: flex;
  gap: 20px;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}

/* Wide enough that a group name and its cadence chip sit on one line without
   wrapping, since that pairing is the thing being scanned down the column. */
.list-col {
  flex: 0 0 400px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* Scrolls as a whole, which is what the schedule form needs. In the detail
   view the content above the history is short, so .history-scroll takes the
   leftover height and this never produces a second scrollbar. */
.detail-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

/* This column is a flex container, so its children would otherwise pick up
   .field's `flex: 1` and stretch vertically -- that rule is meant for fields
   sitting side by side in a .row, not stacked down the pane. Everything here
   keeps its natural height and the column scrolls instead. */
.detail-col > * {
  flex: 0 0 auto;
}

/* The two exceptions: they absorb the leftover height rather than scrolling
   the whole column. */
.detail-col > .list-panel,
.detail-col > .empty-state {
  flex: 1 1 auto;
  min-height: 0;
}

.label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.label-row label {
  font-weight: 600;
}

/* Names the pane's subject, so it outranks the section labels around it. */
.pane-title {
  font-size: 1.2em;
  font-weight: 700;
  overflow-wrap: anywhere;
}

/* Labels the title so a bare string like "military" is unambiguous. Muted and
   lighter than the name it introduces, so it reads as a caption rather than
   competing with the value. */
.pane-title-key {
  font-weight: 600;
  color: #5b6b7d;
}

.history-note {
  margin-top: 22px;
}

/* ---- group list ---- */
/* Bordered box shared by the groups list and the policy history, so both
   scroll regions on this panel have one shape and one header treatment. */
.list-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border: 1px solid #6c757d;
  border-radius: 4px;
  overflow: hidden;
}

/* The list's column heading. Filled, uppercased and separated by the same
   rule the rows use, so it cannot be mistaken for the first row of data --
   which is what a plain bold label above the box was doing. */
.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: #eceff2;
  border-bottom: 1px solid #6c757d;
  font-size: 0.84em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4a5460;
  flex: 0 0 auto;
}

.group-list {
  list-style: none;
  padding: 0;
  margin: 0;
  /* Takes the box's leftover height and scrolls, so a deployment with many
     groups cannot stretch the panel. */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* An action sitting in the header opts out of the heading treatment -- it is a
   control, not part of the label, and .link-btn's own 0.85em would compound
   with the header's to something unreadable. */
.list-header .link-btn {
  font-size: 1em;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

/* State messages inside a .list-panel need the padding the rows carry. */
.pad {
  padding: 12px 14px;
  margin: 0;
}

/* Each entry runs to three lines (name + cadence, period, any pending
   change), so a faint rule let consecutive groups blur together. Matches the
   divider weight used in the policy history. */
.group-list li {
  padding: 12px 14px;
  border-bottom: 1px solid #6c757d;
  cursor: pointer;
}

.group-list li:last-child {
  border-bottom: none;
}

.group-list li:hover {
  background: #f3f5f7;
}

.group-list li.selected {
  background: #e7f1ff;
  box-shadow: inset 3px 0 0 var(--primary-color);
}

.group-list li.unconfigured {
  background: #fffaf0;
}

.group-list li.unconfigured.selected {
  background: #fdf0d5;
}

/* The selected row's tint is close enough to the chip fill to swallow it, so
   the chip goes solid white to stay legible on both selected variants. */
.group-list li.selected .g-cadence {
  background: #fff;
}

.g-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
}

.g-name {
  font-weight: 600;
  font-size: 0.95em;
  overflow-wrap: anywhere;
}

/* The interval and start day ARE the configuration -- everything else on the
   row is derived from them. Rendered as a chip so the value carries weight
   rather than reading as another line of grey metadata. */
.g-cadence {
  font-size: 0.82em;
  font-weight: 700;
  color: #1c4e80;
  background: #eaf2fc;
  border: 1px solid #cfe0f5;
  border-radius: 10px;
  padding: 1px 9px;
  white-space: nowrap;
}

.g-sub {
  font-size: 0.8em;
  color: #5c6570;
  margin-top: 4px;
}

.g-cadence.warn {
  color: #8a5a00;
  background: #fdf1d8;
  border-color: #edc98a;
}

.g-sub.warn {
  color: #a4762c;
}

.g-pending {
  font-size: 0.76em;
  color: #1c4e80;
  margin-top: 6px;
}

/* Filled rather than tinted, so a pending change is not mistaken for the
   cadence chip above it -- one is what is in force, the other is what is
   coming. */
.badge {
  display: inline-block;
  padding: 0 7px;
  margin-right: 5px;
  border-radius: 9px;
  background: #1c4e80;
  color: #fff;
  font-weight: 600;
}

/* ---- detail summary ---- */
/* The cadence in force is the answer to the question this page is opened to
   ask, so it is the loudest thing in the pane: tinted fill, a primary accent
   edge, and values set well above body size. The muted grey card it replaced
   read as a footnote to the history below it. */
.summary {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  padding: 14px 18px;
  margin-bottom: 4px;
  background: #eaf2fc;
  border: 1px solid #cfe0f5;
  border-left: 4px solid var(--primary-color);
  border-radius: 4px;
}

.summary > div,
.impact > div {
  display: flex;
  flex-direction: column;
}

.summary .k,
.impact .k {
  font-size: 0.88em;
  color: #56677a;
  margin-bottom: 3px;
}

.summary .v {
  font-size: 1.18em;
  font-weight: 700;
  color: #16324f;
  line-height: 1.25;
}

/* ---- history timeline ---- */
/* Boxed to match .group-list. A scroll region with no boundary reads as
   content that was mysteriously cut off; the border makes it obvious that
   the list continues past the fold, and gives the two scrollable areas on
   this page the same shape. Padding keeps the timeline rail off the frame. */
.history-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 14px;
  background: #fdfdfd;
}

.history {
  list-style: none;
  padding: 0;
  margin: 0;
  border-left: 2px solid #6c757d;
}

/* Hairline between records. Policy rows wrap onto a note line and a status
   line, so without a rule the eye cannot tell where one policy ends and the
   next begins -- the dots alone are too far left to separate them. */
.history li {
  position: relative;
  padding: 11px 0 11px 20px;
}

/* Drawn as a pseudo-element rather than a border-bottom so it can start
   clear of the timeline rail instead of butting into it. The inset matches
   the li's padding-left, so the rule begins where the record's text does. */
.history li::after {
  content: "";
  position: absolute;
  left: 20px;
  right: 0;
  bottom: 0;
  border-bottom: 1px solid #6c757d;
}

.history li:last-child::after {
  display: none;
}

.history li:last-child {
  padding-bottom: 0;
}

.history .dot {
  position: absolute;
  left: -7px;
  /* Tracks the li's top padding so the dot sits on the first text line. */
  top: 17px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #cfd4d9;
  border: 2px solid #fff;
}

.history li.scheduled .dot {
  background: #4a90d9;
}

.history li.active .dot {
  background: #2a8a3e;
}

.h-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.status-tag {
  font-size: 0.8em;
  font-weight: 700;
  text-transform: uppercase;
  color: #777;
  min-width: 88px;
}

.history li.scheduled .status-tag {
  color: #1c4e80;
}

.history li.active .status-tag {
  color: #2a8a3e;
}

.h-detail {
  font-size: 0.98em;
}

.who {
  font-size: 0.85em;
  color: #777;
}

.locked {
  font-size: 0.85em;
  opacity: 0.5;
}

/* Indent matches .status-tag's min-width plus the .h-main gap, so a note
   lines up under the policy text rather than under its status label. */
.note {
  margin: 4px 0 0 98px;
  font-size: 0.88em;
  color: #666;
}

/* ---- form, matching ModelConfigPanel's .row / .field ---- */
.row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.field {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  flex: 1;
  min-width: 180px;
}

.field label {
  margin-bottom: 6px;
  font-weight: 600;
}

.field input,
.field select {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.violations {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
}

.violations li {
  padding: 8px 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  font-size: 0.84em;
  line-height: 1.45;
}

.violations li.warning {
  background: #fff8e6;
  border: 1px solid #f0c36d;
  color: #7a5b00;
}

.violations li.error {
  background: #fdf1f0;
  border: 1px solid #e9b3ae;
  color: #a4262c;
}

.impact {
  display: flex;
  gap: 28px;
}

.impact .n {
  font-size: 1.3em;
  font-weight: 700;
}

.confirm-toggle {
  display: block;
  font-size: 0.9em;
  background: #fff8e6;
  border: 1px solid #f0c36d;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 8px;
}

.link-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0;
  font-size: 0.85em;
  text-decoration: underline;
}

.link-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.button-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.btn {
  padding: 10px 20px;
  font-size: 16px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn.primary {
  background-color: var(--primary-color);
}

.btn.secondary {
  background-color: #6c757d;
}

/* Destructive. Withdrawing is the only removal on the page, so it carries the
   warning color rather than sitting as a quiet text link among the metadata. */
.btn.danger {
  background-color: #a4262c;
}

/* Header-height variant, for a .btn sitting inline in a .label-row next to a
   title or within a history row rather than in the footer .button-row. */
.btn.small {
  padding: 7px 15px;
  font-size: 0.9em;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Below the split's comfortable width the two columns stack, list first.
   Stacked, a fixed panel height would squeeze both regions into slivers, so
   the panel grows with its content again and each list gets its own cap. */
@media (max-width: 900px) {
  .cadence-config {
    height: auto;
  }

  .split {
    flex-direction: column;
    flex: 0 1 auto;
  }

  .list-col {
    flex: 1 1 auto;
    width: 100%;
  }

  .detail-col {
    overflow-y: visible;
  }

  .list-panel {
    flex: 0 1 auto;
    max-height: 460px;
  }
}
</style>
